import * as STD from '@emersya/mpa-std-lib';

import CM                                  from '@emersya/mpa-components-manager-lib';
import { Section, Image, Text }            from '@emersya/mpa-components-lib';
import ViewerComponent, { NS as ViewerNS } from '../../../Viewer/Viewer';
import { NS as ViewerGraphicsNS }          from '../../../Graphics/ViewerGraphics';

export interface IPlannerWallsTileComponentConfiguration extends Section.Configuration {
	forceBackgroundColor? : string | null;
};

export class PlannerWallsTileComponent extends Section.Component {
	viewerNode : ViewerComponent.ProductInstance | null = null;

	get wallsEditor() {
		return <PlannerWallsEditorNS.Component> this.component_parent?.component_parent;
	};

	async viewerNodeInsert() {
		const product = app.currentProduct;
		if (this.viewerNode || !product || !app.viewer.planner) {
			if (app.isReloading) {
				return;
			}
			this.component_throwUserError('');
		}
		if (this.component_isDisabled) {
			await this.component_enable();
		}
		const wallProductSKU = `@wall-${ product.id }`;
		const wallProductSchema = this.wallsEditor.plannerCategory.products.find(x => x.SKU === wallProductSKU);
		if (!wallProductSchema) {
			this.component_throwLibraryError('');
		}
		if (app.isReloading) {
			if (!app.previousConfigurationMetadata) {
				this.component_throwLibraryError('');
			}
			const vPath = app.viewer.component_getPath();
			const vUI : [ string, ViewerNS.ValueSerialized ] | undefined = <any> app.previousConfigurationMetadata.userInputs.find(
				x => x[ 0 ] === vPath);
			if (!vUI) {
				this.component_throwLibraryError('');
			}

			const savedInstance = vUI[ 1 ].productInstances.find(x => x.product === wallProductSKU && x.metadata.id === this.id);
			if (!savedInstance) {
				if (!this.component_isDisabled) {
					await this.component_disable();
				}
				// Wall was disabled and so not saved.
				return;
			}
			const allInstances = Array.from(app.viewer.safe.value.productInstances);
			const node = allInstances.find(x => x.localId === savedInstance.localId);
			if (!node) {
				this.component_throwUserError('', {});
			}
			this.viewerNode = node;
		} else {
			this.viewerNode = await wallProductSchema.instantiate({
				rootLocalId : app.viewer.globalLocalId,
				appendType : 'insert',
				basketPart : [],
				basketUseDynamicTitleBinding : false,
				metadata : {
					id :           this.id,
					customImages : [
						{
							enabled :   false,
							leftRight : 0,
							upDown :    0,
							resize :    1,
							rotate :    0,
							image :     null
						}
					]
				}
			});
		}
		if (!this.viewerNode) {
			this.component_throwLibraryError('');
		}
		const ciData = this.viewerNode?.metadata?.customImages?.[ 0 ];
		if (ciData) {
			await (<ViewerGraphicsNS.Component> this.component_find('@:content.graphic-0')).updateValue(<any> ciData);
		}
		this.viewerNode.setName('Wall ' + this.id);


	};

	async viewerNodeRemove() {
		if (app.isReloading) {
			return;
		}
		if (!this.component_isDisabled) {
			await this.component_disable();
		}
		if (!this.viewerNode) {
			return;
		}
		await this.viewerNode.delete();
		this.viewerNode = null;
	};

	getWDH() {
		const p = app.currentProduct;
		if (!p) {
			this.component_throwUserError('');
		}
		const dpart = p.getPart([ 'dimensions' ]);
		return {
			width :  Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1),
			depth :  Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1),
			height : Number.parseFloat(dpart.getPart([ 'height' ])?.reference?.value || <string> <any> 1)
		};
	};

	getWallSize() {
		const WDH = this.getWDH();
		const layoutId = this.wallsEditor.layoutId;
		const size = {
			width :  0,
			height : WDH.height - app.getFloorThickness()
		};
		if (this.id === 'back') {
			if (layoutId === 'back') {
				size.width = WDH.width;
			} else if (layoutId === 'left-back-right') {
				size.width = WDH.width + (this.getWallThickness() * 2);
			} else {
				size.width = WDH.width + this.getWallThickness();
			}
		} else {
			size.width = WDH.depth;
		}
		return size;
	};

	getGraphicsLocalId(customImageId : string) {
		void customImageId;
		if (!this.viewerNode) {
			return -1;
		}
		return <number> this.viewerNode.idMapping[ 'customImage' ];
	};

	getGraphicsSizeX(customImageId : string) {
		void customImageId;
		if (!this.viewerNode) {
			return -1;
		}
		return this.getWallImageCustomizationZone().size.x;
	};

	getGraphicsSizeY(customImageId : string) {
		if (!this.viewerNode) {
			return -1;
		}
		void customImageId;
		return this.getWallImageCustomizationZone().size.y;
	};

	getGraphicsMesh(customImageId : string) {
		if (!this.viewerNode) {
			return -1;
		}
		void customImageId;
		return 'PATCH_CLOISON';
	};

	getGraphicsMaterial() {
		return {
			name :          'PATCH_CLOISON',
			configuration : {
				enabled :  'PATCH_CLOISON',
				disabled : 'off'
			}
		};
	};

	getGraphicsProductInstance() {
		return this.viewerNode;
	};

	/** STANDING specific! **/
	getWallImageCustomizationZone() {
		const CP = app.currentProduct;
		const defaultOutput = {
			offset : {
				left :  0,
				right : 0
			},
			size :   {
				x : 0,
				y : 0
			},
			isCut : false,
			cut : {
				start : 0,
				end : 0
			}
		};
		if (!CP) {
			return defaultOutput;
		}
		const layoutPart = CP.getPart([ 'layout' ]);
		if (!layoutPart.currentOption || !layoutPart.currentOption.reference) {
			return defaultOutput;
		}
		const layout = layoutPart.currentOption.reference.value.replace('shape-', '');
		if ((layout === 'no-wall') || !layout.includes(this.id)) {
			return defaultOutput;
		}
		const partitionsHeadsComponent = <StandingPartitionsHeadsNS.Component> (<any> app.modules.used.find(x => x.id ===
			'standing-partitionsHeads')).component;
		const storageComponent = <StandingStorageNS.Component> (<any> app.modules.used.find(x => x.id ===
			'standing-storage')).component;
		const hasPartitionsHead = !!partitionsHeadsComponent.partitionsPosition &&
			partitionsHeadsComponent.partitionsPosition.includes(this.id) &&
			(partitionsHeadsComponent.partitionsSpace === 'vertical') &&
			partitionsHeadsComponent.isPartitionsHeads;
		let hasStorage = storageComponent.hasStorage();
		let storageIsLeftOrRight : 'left' | 'right' | null = null;// left or right from back wall
		if(CP.id === 'siae'){
			if (hasStorage) {
				if (this.id === 'left') {
					if (layout.includes('left')) {
						storageIsLeftOrRight = 'left';
					} else {
					}
				} else if (this.id === 'back') {
					if (layout === 'back' || layout.includes('left')) {
						storageIsLeftOrRight = 'left';
					} else if (layout === 'back-right') {
						storageIsLeftOrRight = 'right';
					} else {
					}
				} else if (this.id === 'right') {
					if (layout === 'back-right') {
						storageIsLeftOrRight = 'right';
					} else {
					}
				} else {
				}
			} else {
			}
			if (!storageIsLeftOrRight) {
				hasStorage = false;
			}
			const offsets = {
				left :  0,
				right : 0
			};
			if (hasPartitionsHead) {
				if (!partitionsHeadsComponent.partitionsWidth || !partitionsHeadsComponent.partitionsPosition) {
					return defaultOutput;
				}
				if (this.id === 'left') {
					offsets.left += partitionsHeadsComponent.partitionsWidth;
				} else if (this.id === 'back') {
					if (partitionsHeadsComponent.partitionsPosition.includes('left')) {
						offsets.right += partitionsHeadsComponent.partitionsWidth;
					} else if (partitionsHeadsComponent.partitionsPosition.includes('right')) {
						offsets.left += partitionsHeadsComponent.partitionsWidth;
					} else {
						if(partitionsHeadsComponent.partitionsPosition === 'back'){
							if(layout.includes('left')){
								offsets.right += partitionsHeadsComponent.partitionsWidth;
							}else{
								offsets.left += partitionsHeadsComponent.partitionsWidth;
							}
						}else{
							offsets.left += partitionsHeadsComponent.partitionsWidth;
							offsets.right += partitionsHeadsComponent.partitionsWidth;
						}
					}
				} else if (this.id === 'right') {
					offsets.right += partitionsHeadsComponent.partitionsWidth;
				}
			}
			if (hasStorage) {
				const space = storageComponent.usedSpace();
				if (!space) {
					this.component_throwLibraryError('');
				}
				if (storageIsLeftOrRight === 'left') {
					if (this.id === 'left') {
						offsets.right += space.lateral;
					} else if (this.id === 'back') {
						offsets.left += space.back;
					}
				} else {
					if (this.id === 'back') {
						offsets.right += space.back;
					} else if (this.id === 'right') {
						offsets.left += space.lateral;
					}
				}
			}
			const size = this.getWallSize();
			const visibleSize = this.getVisibleWallSize();
			const visibleOffset = STD.math.round(size.width - visibleSize.width, 4);
			const finalsOffsets = {
				left :  offsets.left,
				right : offsets.right
			};
			if (this.id === 'back') {
				if (layout === 'left-back-right') {
					finalsOffsets.left += STD.math.round(visibleOffset / 2, 4);
					finalsOffsets.right += STD.math.round(visibleOffset / 2, 4);
				} else if (layout === 'back-left') {
					finalsOffsets.left += visibleOffset;
				} else if (layout === 'back-right') {
					finalsOffsets.right += visibleOffset;
				}
			}
			const out = {
				offset : finalsOffsets,
				size :   {
					x : size.width - (finalsOffsets.left + finalsOffsets.right),
					y : size.height
				},
				isCut : false,
				cut : {
					start : 0,
					end : 0
				}
			};
			return out;
		}else if(CP.id === 'business'){
			const archComponent = <StandingArch.Component> (<any> app.modules.used.find(x => x.id ===
				'standing-arch')).component;
			const archDimensions = archComponent.archDimensions;
			if(!archDimensions){
				return defaultOutput;
			}
			const wdh = this.getWDH();
			if (hasStorage) {
				if (this.id === 'left') {
					if (layout === 'back-left') {
						storageIsLeftOrRight = 'right';
					} else {
					}
				} else if (this.id === 'back') {
					if (layout === 'back' || (layout.includes('left') && layout.includes('right'))) {
						storageIsLeftOrRight = 'left';
					} else {
					}
				} else if (this.id === 'right') {
					if (layout === 'back-right') {
						storageIsLeftOrRight = 'left';
					} else {
					}
				} else {
				}
			} else {
			}
			if (!storageIsLeftOrRight) {
				hasStorage = false;
			}
			const offsets = {
				left :  0,
				right : 0
			};
			if (hasPartitionsHead) {
				if (!partitionsHeadsComponent.partitionsWidth || !partitionsHeadsComponent.partitionsPosition) {
					return defaultOutput;
				}
				if (this.id === 'left') {
					offsets.left += partitionsHeadsComponent.partitionsWidth;
				} else if (this.id === 'back') {
					if (partitionsHeadsComponent.partitionsPosition.includes('left')) {
						offsets.right += partitionsHeadsComponent.partitionsWidth;
					} else if (partitionsHeadsComponent.partitionsPosition.includes('right')) {
						offsets.left += partitionsHeadsComponent.partitionsWidth;
					} else {
						if(partitionsHeadsComponent.partitionsPosition === 'back'){
							if(layout.includes('left')){
								offsets.right += partitionsHeadsComponent.partitionsWidth;
							}else{
								offsets.left += partitionsHeadsComponent.partitionsWidth;
							}
						}else{
							offsets.left += partitionsHeadsComponent.partitionsWidth;
							offsets.right += partitionsHeadsComponent.partitionsWidth;
						}
					}
				} else if (this.id === 'right') {
					offsets.right += partitionsHeadsComponent.partitionsWidth;
				}
			}
			let isCut = false;
			let cut = {
				start : 0,
				end : 0
			};
			if (hasStorage) {
				const space = storageComponent.usedSpace();
				if (!space) {
					this.component_throwLibraryError('');
				}
				isCut = true;
				if (storageIsLeftOrRight === 'left') {
					if(layout === 'back'){
						isCut = false;
						offsets.left += space.back;
					}else if(layout === 'back-right'){
						cut.start = archDimensions.width;
						cut.end = archDimensions.width + space.lateral;
					}else{
						cut.start = archDimensions.width;
						cut.end = archDimensions.width + space.back;
					}
				} else {
					if(layout === 'back-left'){
						cut.start = wdh.depth - archDimensions.width - space.lateral;
						cut.end = wdh.depth - archDimensions.width;
					}
				}
			}
			const size = this.getWallSize();
			const visibleSize = this.getVisibleWallSize();
			const visibleOffset = STD.math.round(size.width - visibleSize.width, 4);
			const finalsOffsets = {
				left :  offsets.left,
				right : offsets.right
			};
			if (this.id === 'back') {
				if (layout === 'left-back-right') {
					finalsOffsets.left += STD.math.round(visibleOffset / 2, 4);
					finalsOffsets.right += STD.math.round(visibleOffset / 2, 4);
				} else if (layout === 'back-left') {
					finalsOffsets.left += visibleOffset;
				} else if (layout === 'back-right') {
					finalsOffsets.right += visibleOffset;
				}
			}
			const out = {
				offset : finalsOffsets,
				size :   {
					x : size.width - (finalsOffsets.left + finalsOffsets.right),
					y : size.height
				},
				isCut : isCut,
				cut : cut
			};
			return out;
		}else if(CP.id === 'prestige'){
			const archComponent = <StandingArch.Component> (<any> app.modules.used.find(x => x.id ===
				'standing-arch')).component;
			const archDimensions = archComponent.archDimensions;
			if(!archDimensions){
				return defaultOutput;
			}
			const wdh = this.getWDH();
			if (hasStorage) {
				if (this.id === 'left') {
					if (layout === 'back-left') {
						storageIsLeftOrRight = 'right';
					}else{
					}
				} else if (this.id === 'back') {
					if (layout === 'back' || layout === 'left-back-right' || layout === 'back-left') {
						storageIsLeftOrRight = 'left';
					} else if( layout === 'back-right') {
						storageIsLeftOrRight = 'right';
					}
				} else if (this.id === 'right') {
					if (layout === 'back-right') {
						storageIsLeftOrRight = 'left';
					} else {
					}
				} else {
				}
			} else {
			}
			if (!storageIsLeftOrRight) {
				hasStorage = false;
			}
			const offsets = {
				left :  0,
				right : 0
			};
			let isCut = false;
			let cut = {
				start : 0,
				end : 0
			};
			if (hasStorage) {
				const space = storageComponent.usedSpace();
				if (!space) {
					this.component_throwLibraryError('');
				}
				isCut = true;
				if (storageIsLeftOrRight === 'left') {
					if(layout === 'back-right'){
						isCut = false;
						offsets.left += space.lateral;
					}else if(layout === 'left-back-right'){
						isCut = true;
						cut.start = archDimensions.width;
						cut.end = archDimensions.width + space.back;
					}else{
						isCut = false;
						offsets.left += space.back;
					}
				} else {
					if(layout === 'back-right'){
						isCut = false;
						offsets.right += space.back;
					}else{
						isCut = false;
						offsets.right += space.lateral;
					}
					cut.start = wdh.depth - archDimensions.width - space.lateral;
					cut.end = wdh.depth - archDimensions.width;
				}
			}
			const size = this.getWallSize();
			const visibleSize = this.getVisibleWallSize();
			const visibleOffset = STD.math.round(size.width - visibleSize.width, 4);
			const finalsOffsets = {
				left :  offsets.left,
				right : offsets.right
			};
			if (this.id === 'back') {
				if (layout === 'left-back-right') {
					finalsOffsets.left += STD.math.round(visibleOffset / 2, 4);
					finalsOffsets.right += STD.math.round(visibleOffset / 2, 4);
				} else if (layout === 'back-left') {
					finalsOffsets.left += visibleOffset;
				} else if (layout === 'back-right') {
					finalsOffsets.right += visibleOffset;
				}
			}
			const out = {
				offset : finalsOffsets,
				size :   {
					x : size.width - (finalsOffsets.left + finalsOffsets.right),
					y : size.height
				},
				isCut : isCut,
				cut : cut
			};
			return out;
		}else if(CP.id === 'confort'){
			if (hasStorage) {
				if (this.id === 'left') {
					if (layout.includes('left')) {
						storageIsLeftOrRight = 'right';
					} else {
					}
				} else if (this.id === 'back') {
					if (layout === 'back' || layout.includes('left')) {
						storageIsLeftOrRight = 'left';
					} else if (layout === 'back-right') {
						storageIsLeftOrRight = 'right';
					} else {
					}
				} else if (this.id === 'right') {
					if (layout === 'back-right') {
						storageIsLeftOrRight = 'left';
					} else {
					}
				} else {
				}
			} else {
			}
			if (!storageIsLeftOrRight) {
				hasStorage = false;
			}
			const offsets = {
				left :  0,
				right : 0
			};
			if (hasStorage) {
				const space = storageComponent.usedSpace();
				if (!space) {
					this.component_throwLibraryError('');
				}
				if (storageIsLeftOrRight === 'left') {
					if(layout === 'back-right'){
						offsets.left += space.lateral;
					}else{
						offsets.left += space.back;
					}
				} else {
					if(layout === 'back-right'){
						offsets.right += space.back;
					}else{
						offsets.right += space.lateral;
					}
				}
			}
			const size = this.getWallSize();
			const visibleSize = this.getVisibleWallSize();
			const visibleOffset = STD.math.round(size.width - visibleSize.width, 4);
			const finalsOffsets = {
				left :  offsets.left,
				right : offsets.right
			};
			if (this.id === 'back') {
				if (layout === 'left-back-right') {
					finalsOffsets.left += STD.math.round(visibleOffset / 2, 4);
					finalsOffsets.right += STD.math.round(visibleOffset / 2, 4);
				} else if (layout === 'back-left') {
					finalsOffsets.left += visibleOffset;
				} else if (layout === 'back-right') {
					finalsOffsets.right += visibleOffset;
				}
			}
			const out = {
				offset : finalsOffsets,
				size :   {
					x : size.width - (finalsOffsets.left + finalsOffsets.right),
					y : size.height
				},
				isCut : false,
				cut : {
					start : 0,
					end : 0
				}
			};
			return out;
		}else{
			return defaultOutput;
		}
	};

	getVisibleWallSize() {
		const WDH = this.getWDH();
		const size = {
			width :  0,
			height : WDH.height - app.getFloorThickness()
		};
		if (this.id === 'back') {
			size.width = WDH.width;
		} else {
			size.width = WDH.depth;
		}
		return size;
	};

	getWallThickness() {
		// TODO : DEHARDCODE
		return 0.05;
	};

	computeMatrix() : ViewerComponent.TMatrix {
		const groundSize = this.getWDH();
		const wallSize = this.getWallSize();
		const layoutId = this.wallsEditor.layoutId;
		if (this.id === 'left') {
			if (layoutId === 'left-back-right') {
				return (ViewerComponent.mtx(
					[ -(groundSize.width / 2), 0, groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					90
				));
			} else {
				return (ViewerComponent.mtx(
					[ -(groundSize.width / 2), 0, groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					90
				));
			}
		} else if (this.id === 'back') {
			if (layoutId === 'back') {
				return (ViewerComponent.mtx(
					[ -groundSize.width / 2, 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				));
			} else if (layoutId === 'back-left') {
				return (ViewerComponent.mtx(
					[ -groundSize.width / 2 - this.getWallThickness(), 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				));
			} else if (layoutId === 'back-right') {
				return ViewerComponent.mtx(
					[ -groundSize.width / 2, 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				);
			} else {
				return ViewerComponent.mtx(
					[ -groundSize.width / 2 - this.getWallThickness(), 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				);
			}
		} else { // Right
			if (layoutId === 'left-back-right') {
				return (ViewerComponent.mtx(
					[ groundSize.width / 2, 0, -groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					-90
				));
			} else {
				return (ViewerComponent.mtx(
					[ groundSize.width / 2, 0, -groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					-90
				));
			}
		}
	};

	computeCustomImageMatrix() {
		const baseSize = this.getWallSize();
		const zone = this.getWallImageCustomizationZone();
		return (ViewerComponent.mtx(
			[ zone.offset.left / baseSize.width, 0, 0.01 ],
			[ zone.size.x / baseSize.width, zone.size.y / baseSize.height, 1 ],
			0
		));
	};

	async viewerNodeUpdate(stopBugSymbol? : Symbol) {
		if (!this.viewerNode || app.isReloading) {
			return;
		}
		const batch : ViewerComponent.IBatchDescriptor = [
			[
				'setProductNodesInstancesMatrices',
				[
					{
						localId : this.viewerNode.localId,
						matrix :  this.computeMatrix()
					},
					{
						localId : <number> this.viewerNode.idMapping[ 'customImage' ],
						matrix :  this.computeCustomImageMatrix()
					}
				]
			]
		];
		const md = <any> this.viewerNode.metadata;
		const ciData = <any | undefined> md?.customImages[ 0 ];
		const zoneCI = this.getWallImageCustomizationZone();
		if (!ciData?.enabled || !ciData.image) {
			batch.push([
				'transformMeshInstancesTextures',
				[
					{
						name :        'PATCH_CLOISON',
						nodeLocalId : <number> this.viewerNode.idMapping[ 'customImage' ],
						transform :   {
							translation :    [ 0, 0 ],
							scaling :        [ 1, 1 ],
							rotationCenter : [ 0, 0 ],
							rotation :       0,
							scaleX :         zoneCI.size.x,
							scaleY :         zoneCI.size.y,
							offsetX :        zoneCI.offset.left,
							offsetY :        0
						}
					}
				]
			]);
		}
		await app.viewer.batch(batch);
		if (ciData?.enabled && ciData.image) {
			const TMIT = ciData.viewerInfos.transformMeshInstanceTexture;
			ciData.viewerInfos = app.viewer.transformMeshInstanceTexture_helper({
				image : {
					resolutionX : ciData.image.naturalWidth,
					resolutionY : ciData.image.naturalHeight,
					left :        ciData.leftRight,
					up :          ciData.upDown,
					scale :       ciData.resize,
					rotation :    STD.math.degToRad(ciData.rotate)
				},
				zone :  {
					mesh :        TMIT.name,
					width :       zoneCI.size.x,
					height :      zoneCI.size.y,
					nodeLocalId : <number> this.viewerNode.idMapping[ 'customImage' ]
				}
			});
			//!!!!!!!! Viewer say batch was terminated but is not the case so we must wait!!!!!!
			setTimeout(async () => {
				await app.viewer.batch([
					[ 'transformMeshInstancesTextures', [ TMIT ] ]
				]);
			}, 250);
			// batch.unshift([ 'transformMeshInstancesTextures', [ TMIT ] ]);
			if (stopBugSymbol !== Symbol.for('stopBug')) {
				await this.viewerNodeUpdate(Symbol.for('stopBug'));
			}
		}
	};

	getMenuConfigurator() {
		return this.component_find('@:content');
	};

	async updateCustomizableAreaInfo() {
		const textWidth = <Text.Component> this.component_find('@:content.graphicArea.width');
		const textHeight = <Text.Component> this.component_find('@:content.graphicArea.height');
		const imgs = [
			this.component_find('@:content.graphicArea.img-full'),
			this.component_find('@:content.graphicArea.img-ph'),
			this.component_find('@:content.graphicArea.img-ph-s'),
			this.component_find('@:content.graphicArea.img-s'),
			this.component_find('@:content.graphicArea.img-ph-right'),
			this.component_find('@:content.graphicArea.img-s-ph'),
			this.component_find('@:content.graphicArea.img-s-right'),
			this.component_find('@:content.graphicArea.img-ph-left-right')
		].reduce<Record<string, CM.DOMComponent<any, any, any>>>((a, c) => {
			a[ c.id.replace('img-', '') ] = <CM.DOMComponent<any, any, any>> c;
			return a;
		}, {});
		const PH = <StandingPartitionsHeadsNS.Component> app.modules.used.find(x => x.id ===
			'standing-partitionsHeads')?.component;
		const S = <StandingStorageNS.Component> app.modules.used.find(x => x.id === 'standing-storage')?.component;
		const LE = <PlannerLayoutEditorNS.Component> app.modules.used.find(x => x.id ===
			'viewer-planner-layout-editor')?.component;
		const layout = LE.value?.id.replace('shape-', '') || '';
		if (!layout) {
			return;
		}
		const hasPartitionHead = PH.isPartitionsHeads && PH.partitionsPosition && PH.partitionsPosition.includes(this.id);
		const hasStorage = S.hasStorage() && (
			(this.id === 'left') ? layout.includes('left') :
				(this.id === 'back') ? true :
					(this.id === 'right') ? layout === 'back-right' :
						false
		);
		await imgs[ 'full' ]?.component_enable();
		await imgs[ 'ph' ]?.component_enable();
		await imgs[ 'ph-s' ]?.component_enable();
		await imgs[ 's' ]?.component_enable();
		await imgs[ 'ph-right' ]?.component_enable();
		await imgs[ 's-ph' ]?.component_enable();
		await imgs[ 's-right' ]?.component_enable();
		await imgs[ 'ph-left-right' ]?.component_disable();
		textHeight.component_DOM.classList.remove('larger');
		if (!hasPartitionHead && !hasStorage) {
			await imgs[ 'ph' ]?.component_disable();
			await imgs[ 'ph-s' ]?.component_disable();
			await imgs[ 's' ]?.component_disable();
			await imgs[ 'ph-right' ]?.component_disable();
			await imgs[ 's-ph' ]?.component_disable();
			await imgs[ 's-right' ]?.component_disable();
		} else {
			await imgs[ 'full' ]?.component_disable();
			if (hasPartitionHead && hasStorage) {
				await imgs[ 'ph' ]?.component_disable();
				await imgs[ 's' ]?.component_disable();
				await imgs[ 'ph-right' ]?.component_disable();
				await imgs[ 's-right' ]?.component_disable();
				if (layout === 'back-right') {
					if (this.id === 'right') {
						await imgs[ 'ph-s' ]?.component_disable();
					} else {
						await imgs[ 's-ph' ]?.component_disable();
					}
				} else if (layout === 'back') {
					await imgs[ 'ph-s' ]?.component_disable();
				} else if (layout === 'back-left') {
					if (this.id === 'back') {
						await imgs[ 'ph-s' ]?.component_disable();
					} else {
						await imgs[ 's-ph' ]?.component_disable();
					}
				} else if (layout === 'left-back-right') {
					if (this.id === 'left') {
						await imgs[ 's-ph' ]?.component_disable();
					} else if (this.id === 'back') {
						await imgs[ 's' ]?.component_enable();
						await imgs[ 'ph-right' ]?.component_disable();
						await imgs[ 's-right' ]?.component_disable();
					} else if (this.id === 'right') {
						await imgs[ 'ph' ]?.component_disable();
					}
				}
			} else if (hasPartitionHead) {
				await imgs[ 'ph-s' ]?.component_disable();
				await imgs[ 's' ]?.component_disable();
				await imgs[ 's-ph' ]?.component_disable();
				await imgs[ 's-right' ]?.component_disable();
				if (this.id === 'left') {
					if (layout === 'back-left') {
						await imgs[ 'ph-right' ]?.component_disable();
					} else if (layout === 'left-back-right') {
						await imgs[ 'ph-right' ]?.component_disable();
					}
				} else if (this.id === 'back') {
					if (layout === 'back-left') {
						await imgs[ 'ph' ]?.component_disable();
					} else if (layout === 'back-right') {
						await imgs[ 'ph-right' ]?.component_disable();
					} else if( layout === 'back'){
						await imgs[ 'ph' ]?.component_disable();
						await imgs[ 'ph-right' ]?.component_disable();
						await imgs[ 'ph-left-right' ]?.component_enable();
						textHeight.component_DOM.classList.add('larger');
					}
				} else  /* right */{
					if (layout === 'back-right') {
						await imgs[ 'ph' ]?.component_disable();
					} else if (layout === 'left-back-right') {
						await imgs[ 'ph' ]?.component_disable();
					}
				}
			} else {
				await imgs[ 'ph' ]?.component_disable();
				await imgs[ 'ph-s' ]?.component_disable();
				await imgs[ 's-ph' ]?.component_disable();
				await imgs[ 's-right' ]?.component_disable();
				if (this.id === 'left') {
					await imgs[ 'ph' ]?.component_disable();
					if (layout === 'back-left') {
						await imgs[ 'ph-right' ]?.component_disable();
					} else if (layout === 'left-back-right') {
						await imgs[ 'ph-right' ]?.component_disable();
					}
				} else if (this.id === 'back') {

					if (layout === 'left-back-right') {
						await imgs[ 's-right' ]?.component_enable();
						await imgs[ 's' ]?.component_disable();
						await imgs[ 'ph-right' ]?.component_disable();
						await imgs[ 'ph-s' ]?.component_disable();
						await imgs[ 's-ph' ]?.component_disable();
						await imgs[ 'ph-s-right' ]?.component_enable();
					} else if (layout === 'back-left') {
						await imgs[ 's' ]?.component_disable();
						await imgs[ 'ph-right' ]?.component_disable();
						await imgs[ 'ph' ]?.component_disable();
						await imgs[ 's-right' ]?.component_enable();
					} else if (layout === 'back-right') {
						await imgs[ 'ph-right' ]?.component_disable();
					} else if (layout === 'back') {
						await imgs[ 'ph-right' ]?.component_disable();
						await imgs[ 's' ]?.component_disable();
						await imgs[ 's-right' ]?.component_enable();
					}
				} else  /* right */{
					if (layout === 'back-right') {
						await imgs[ 's-right' ]?.component_enable();
						await imgs[ 's' ]?.component_disable();
						await imgs[ 'ph-right' ]?.component_disable();
					}
				}
			}
		}
		const { size } = this.getWallImageCustomizationZone();
		textWidth.update((Math.round(size.x * 100) / 100).toString(10) + ' m');
		textHeight.update((Math.round((size.y + 0.01) * 100) / 100).toString(10) + ' m');
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component> [ 0 ],
		configuration : IPlannerWallsTileComponentConfiguration
	)
	{
		super(descriptor, configuration);
		const translationsPlannerWallEditor = app.modules.used.find(x => x.id === 'viewer-planner-walls-editor');
		if (!translationsPlannerWallEditor) {
			this.component_throwUserError('Missing translations for planner walls editor!');
		}
		const translationTitle = translationsPlannerWallEditor.translations[ 'viewer-planner-walls-editor-' + this.id ];
		if (!translationTitle) {
			this.component_throwUserError('Missing translations for planner walls editor ' + this.id);
		}
		const tr = STD.translationsManager.languages.reduce<Record<string, string>>((a, c) => {
			a[ c ] = 'icon header';
			return a;
		}, {});
		configuration[ '@innerConfigurations' ] = {
			'header' : {
				'@innerConfigurations' : {
					'title' :      {
						'@translations' : {
							'text' : translationTitle
						}
					},
					'headerIcon' : <Image.Configuration> {
						'@translations' : {
							'name' : tr
						},
						'URL' :           '/bundles/EmersyaWebsitePro/images/apps/_mpa/engine/0.0.0/arrow.svg'
					}
				}
			},
			content :  {
				'@innerConfigurations' : {
					'graphic-0' : <ViewerGraphicsNS.Configuration> {
						forceBackgroundColor : configuration.forceBackgroundColor
					}
				}
			}
		};
	}
}

export namespace NS {
	export const Component = PlannerWallsTileComponent;
	export type Component = PlannerWallsTileComponent;
	export type Configuration = IPlannerWallsTileComponentConfiguration;
}
import manifestXMLSource                   from './PlannerWallsProductTile.xml';
import './PlannerWallsProductTile.scss';
import { NS as PlannerWallsEditorNS }      from '../Editor/PlannerWallsEditor';
import { NS as PlannerLayoutEditorNS }     from '../../layout/Editor/PlannerLayoutEditor';
import {
	NS as StandingPartitionsHeadsNS
}                                          from '../../../../customers/standing/StandingPartitionsHeads/StandingPartitionsHeads';
import { NS as StandingStorageNS }         from '../../../../customers/standing/StandingStorage/StandingStorage';
import { NS as StandingArch }         	   from '../../../../customers/standing/StandingArch/StandingArch';

CM.declare({
	id :       'viewer-planner-walls-productTile',
	type :     PlannerWallsTileComponent,
	manifest : manifestXMLSource
});
